import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { PAYER_PROGRAMS, PROCEDURE_CODES, PAYER_TYPES } from '../../constants/patientConstants';
import { Info } from 'lucide-react';

/**
 * Pre-selects the patient's primary billing line on a Claim or EVV visit
 * form, with a dropdown to switch to any of the patient's other lines.
 *
 * Shape: each item in `patient.billing_lines` is
 *   `{payer_type, payer_program, procedure_code}`.
 * The control mirrors the selected line back into the form's
 *   `payer_type`, `payer_program`, and `service_code`
 * (service_code == procedure_code on the claim/EVV side).
 *
 * Props:
 *   - patient: the selected patient object (must include `billing_lines`)
 *   - value: { payer_type, payer_program, service_code } — current form values
 *   - onChange: ({payer_type, payer_program, service_code}) => void
 *   - testIdPrefix: e.g. "claim" or "evv"
 *
 * If the patient has no billing lines configured, the component falls back
 * to three independent dropdowns so the user can still pick something.
 */
export const BillingLineSelector = ({ patient, value, onChange, testIdPrefix = 'billing' }) => {
  // Build the canonical list of lines. Falls back to the legacy single
  // payer_type/payer_program/procedure_code on the patient if billing_lines is empty.
  const lines = useMemo(() => {
    if (!patient) return [];
    const list = Array.isArray(patient.billing_lines) ? patient.billing_lines : [];
    if (list.length > 0) return list;
    if (patient.payer_type || patient.payer_program || patient.procedure_code) {
      return [{
        payer_type: patient.payer_type || '',
        payer_program: patient.payer_program || '',
        procedure_code: patient.procedure_code || '',
      }];
    }
    return [];
  }, [patient]);

  const labelFor = (pt, pp, pc) => {
    const ptLabel = PAYER_TYPES.find((p) => p.value === pt)?.label || pt || '—';
    const ppLabel = PAYER_PROGRAMS.find((p) => p.value === pp)?.label || pp || '—';
    const pcLabel = PROCEDURE_CODES.find((p) => p.value === pc)?.label || pc || '—';
    return `${ptLabel}  ·  ${ppLabel}  ·  ${pcLabel}`;
  };

  const selectedKey = `${value?.payer_type || ''}|${value?.payer_program || ''}|${value?.service_code || ''}`;

  const handlePick = (key) => {
    if (key === '__custom__') {
      onChange({ payer_type: '', payer_program: '', service_code: '' });
      return;
    }
    const [pt, pp, pc] = key.split('|');
    onChange({ payer_type: pt, payer_program: pp, service_code: pc });
  };

  if (!patient) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
        <span>Pick a patient first — their billing lines will populate this control.</span>
      </div>
    );
  }

  if (lines.length === 0) {
    // Fallback — patient has no billing lines yet. Show three raw dropdowns
    // so the user can still proceed. The selection here is NOT persisted
    // back to the patient — it only flows onto this claim/visit.
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3" data-testid={`${testIdPrefix}-billing-line-empty`}>
        <div className="text-xs text-amber-900 flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            This patient has no billing lines saved on their profile. Pick a
            payer + program + code here for this entry — and consider opening
            their Patient record to add their default billing lines.
          </span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Payer Type</Label>
            <Select
              value={value?.payer_type || ''}
              onValueChange={(v) => onChange({ ...value, payer_type: v })}
            >
              <SelectTrigger data-testid={`${testIdPrefix}-payer-type-fallback`}>
                <SelectValue placeholder="Pick payer" />
              </SelectTrigger>
              <SelectContent>
                {PAYER_TYPES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Payer Program</Label>
            <Select
              value={value?.payer_program || ''}
              onValueChange={(v) => onChange({ ...value, payer_program: v })}
            >
              <SelectTrigger data-testid={`${testIdPrefix}-payer-program-fallback`}>
                <SelectValue placeholder="Pick program" />
              </SelectTrigger>
              <SelectContent>
                {PAYER_PROGRAMS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Procedure Code</Label>
            <Select
              value={value?.service_code || ''}
              onValueChange={(v) => onChange({ ...value, service_code: v })}
            >
              <SelectTrigger data-testid={`${testIdPrefix}-procedure-code-fallback`}>
                <SelectValue placeholder="Pick code" />
              </SelectTrigger>
              <SelectContent>
                {PROCEDURE_CODES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-billing-line-selector`}>
      <Label>Service Line</Label>
      <Select value={selectedKey} onValueChange={handlePick}>
        <SelectTrigger data-testid={`${testIdPrefix}-billing-line-trigger`}>
          <SelectValue placeholder="Pick a service line" />
        </SelectTrigger>
        <SelectContent>
          {lines.map((line, idx) => {
            const key = `${line.payer_type || ''}|${line.payer_program || ''}|${line.procedure_code || ''}`;
            return (
              <SelectItem
                key={`${key}-${idx}`}
                value={key}
                data-testid={`${testIdPrefix}-billing-line-option-${idx}`}
              >
                <span className="inline-flex items-center gap-2">
                  {idx === 0 ? (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                      Primary
                    </span>
                  ) : null}
                  <span>{labelFor(line.payer_type, line.payer_program, line.procedure_code)}</span>
                </span>
              </SelectItem>
            );
          })}
          <SelectItem value="__custom__" data-testid={`${testIdPrefix}-billing-line-option-custom`}>
            <span className="text-slate-500 italic">Pick a different payer / program / code...</span>
          </SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">
        Patient has {lines.length} billing line{lines.length === 1 ? '' : 's'} on file.
        The primary is preselected — switch to another if this entry was
        delivered under a different payer or program.
      </p>
    </div>
  );
};

export default BillingLineSelector;
